import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Printer, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { adminGetBlob } from '../adminApi';
import { mono, GREEN } from '../bfsi/bfsiShared';
import { qrGet, qrPost, openPdf, fmtDate } from './qrApi';

export function DeviceQrCard({ deviceId }) {
  const [info, setInfo] = useState(null);
  const [png, setPng] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, blob] = await Promise.all([qrGet(`/devices/${deviceId}/qr`), adminGetBlob(`/qr/devices/${deviceId}/qr.png`)]);
      setInfo(i);
      setPng(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch (e) { toast.error(e.message); }
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    try {
      await qrPost(`/devices/${deviceId}/rotate-token`);
      toast.success('Token rotated — the old label now answers 410. Print and apply the new label.');
      setConfirm(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (!info) return null;
  return (
    <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="device-qr-card">
      <p className="text-sm" style={{ color: '#6b7069' }}>Device QR</p>
      <div className="flex gap-4 mt-2">
        {png && <img src={png} alt="Device QR" className="w-24 h-24 border rounded" style={{ borderColor: '#E5E3DC' }} data-testid="device-qr-preview" />}
        <div className="min-w-0 text-xs space-y-1" style={{ color: '#4b504a' }}>
          <div className="truncate" style={mono} data-testid="device-qr-token">{info.qr_token}</div>
          <div className="truncate" style={{ color: '#9a9e96' }}>{info.url}</div>
          <div>{info.qr_token_rotated_at ? `Rotated ${fmtDate(info.qr_token_rotated_at)} · ${info.rotations.length}×` : 'Original token'}</div>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button data-testid="device-print-label" size="sm" className="h-8 text-white gap-1" style={{ background: GREEN }}
          onClick={() => openPdf(`/devices/${deviceId}/label.pdf`).catch(e => toast.error(e.message))}><Printer size={14} /> Print label</Button>
        <Button data-testid="device-rotate-token" size="sm" variant="outline" className="h-8 gap-1 border-[#D9D6CD]" onClick={() => setConfirm(true)}><RefreshCw size={14} /> Rotate token</Button>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="rotate-token-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate the device QR token?</AlertDialogTitle>
            <AlertDialogDescription>
              The label currently on <span style={mono}>{info.trk_id}</span> will stop working (scans show “This label was replaced”).
              You must print and apply the new label.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rotate-token-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="rotate-token-confirm" onClick={rotate} style={{ background: '#B42318' }}>Rotate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
